import { useEffect, useState } from "react";
import api, { formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Receipt, Upload, AlertCircle, Copy, Loader2, Wand2, Building2, User as UserIcon, Mail, MapPin, Hash, FileText, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { RevisoesLista } from "@/components/fiscal/RevisaoNf";

const formVazio = (ins) => ({
  numero: ins?.nf_numero || "",
  data_emissao: ins?.nf_data_emissao || new Date().toISOString().slice(0, 10),
  observacao: ins?.nf_observacao || "",
  file: null,
});

function Field({ label, icon: Ico, children, mono }) {
  return (
    <div className="flex gap-2 items-start">
      {Ico ? <Ico className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" /> : null}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className={`text-slate-800 ${mono ? "font-mono" : ""}`}>{children || <span className="text-slate-400">—</span>}</div>
      </div>
    </div>
  );
}

function Cabecalho({ icon: Ico, children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
      <Ico className="w-3.5 h-3.5" /> {children}
    </div>
  );
}

function Tomador({ orgao }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <Cabecalho icon={Building2}>Tomador do serviço</Cabecalho>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        <Field label="Razão Social" icon={Building2}><span className="font-bold" data-testid="emissao-razao-social">{orgao.razao_social}</span></Field>
        <Field label="CNPJ" icon={Hash} mono><span data-testid="emissao-cnpj">{orgao.cnpj}</span></Field>
        <Field label="Endereço" icon={MapPin}><span data-testid="emissao-endereco">{orgao.endereco}</span></Field>
        <Field label="Cidade / UF" icon={MapPin}>{orgao.cidade}{orgao.uf ? ` / ${orgao.uf}` : ""}</Field>
      </div>
    </div>
  );
}

function Responsavel({ resp, emailInst }) {
  const informado = resp.informado_pela_vendedora;
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${informado ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/60"}`}>
      <Cabecalho icon={UserIcon}>Responsável (para o e-mail da NF)</Cabecalho>
      {informado ? (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid="emissao-resp-informado">
          <CheckCircle2 className="w-3.5 h-3.5" /> Informado pela vendedora na inscrição
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800" data-testid="emissao-resp-sugestao">
          <AlertCircle className="w-3.5 h-3.5" /> Não informado na inscrição · sugestão do cadastro, confira antes de enviar
        </span>
      )}
      <Field label="Nome" icon={UserIcon}>
        <span data-testid="emissao-resp-nome">{resp.nome}</span>
        {resp.funcao ? <span className="text-xs text-slate-500 ml-1">— {resp.funcao}</span> : null}
      </Field>
      <Field label="E-mail" icon={Mail} mono>
        <span className="block break-all" data-testid="emissao-resp-email">{resp.email || emailInst}</span>
      </Field>
    </div>
  );
}

function Venda({ dados, rotulo }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <Cabecalho icon={UserIcon}>Vendedora responsável</Cabecalho>
      <Field label="Nome" icon={UserIcon}>
        <span data-testid="emissao-vendedora">{dados.vendedora?.nome}</span>
        {dados.vendedora?.codigo ? <span className="text-xs text-slate-500 ml-1">· {dados.vendedora.codigo}</span> : null}
      </Field>
      <Field label="Curso" icon={FileText}><span className="font-semibold" data-testid="emissao-curso">{rotulo || dados.curso?.nome}</span></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Valor"><b className="tabular-nums">{formatBRL(dados.valor_vendido)}</b></Field>
        <Field label="Inscritos">
          <b className="tabular-nums">{dados.quantidade}</b>
          {dados.cortesias > 0 && <span className="text-xs text-sky-700 ml-1" data-testid="emissao-cortesias">({dados.quantidade - dados.cortesias} pagantes + {dados.cortesias} cortesia)</span>}
        </Field>
      </div>
    </div>
  );
}

async function copiarTexto(texto) {
  try {
    if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(texto); return true; }
  } catch { /* fallback abaixo */ }
  const ta = document.createElement("textarea");
  ta.value = texto; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.top = "-1000px";
  document.body.appendChild(ta); ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  return ok;
}

function Descricao({ texto }) {
  const copiar = async () => (await copiarTexto(texto)) ? toast.success("Descrição copiada ✓") : toast.error("Não consegui copiar. Selecione manualmente.");
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Descrição sugerida para a NF</div>
        <Button size="sm" variant="outline" onClick={copiar} className="h-7 text-xs" data-testid="btn-copiar-descricao"><Copy className="w-3 h-3 mr-1" /> Copiar</Button>
      </div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="emissao-descricao">{texto}</div>
    </div>
  );
}

function DetectStatus({ detect }) {
  if (detect.status === "detecting") return <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Analisando PDF...</div>;
  if (detect.status === "ok") {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700 font-bold" data-testid="detect-ok">
        <Wand2 className="w-3 h-3" /> Nº detectado automaticamente
        {detect.info?.confianca ? <span className="text-slate-500 font-normal">· confiança {detect.info.confianca}</span> : null}
      </div>
    );
  }
  if (detect.status === "fail") return <div className="mt-1 flex items-center gap-1 text-xs text-amber-700 font-bold" data-testid="detect-fail"><AlertCircle className="w-3 h-3" /> Não consegui detectar o nº — preencha manualmente</div>;
  return null;
}

function ArquivoNf({ nfForm, setNfForm, detect, onFileChange }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
      <Cabecalho icon={Upload}>Arquivo e dados da NF</Cabecalho>
      <div>
        <label className="text-xs font-bold uppercase text-slate-600">Arquivo da NF (PDF, XML)*</label>
        <Input type="file" accept=".pdf,.xml,.png,.jpg,.jpeg" onChange={(e) => onFileChange(e.target.files?.[0] || null)} data-testid="input-nf-file" />
        <DetectStatus detect={detect} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-bold uppercase text-slate-600">Número da NF*</label>
          <Input value={nfForm.numero} onChange={(e) => setNfForm({ ...nfForm, numero: e.target.value })} placeholder="Ex.: 000123" data-testid="input-nf-numero" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-slate-600">Data de emissão*</label>
          <Input type="date" value={nfForm.data_emissao} onChange={(e) => setNfForm({ ...nfForm, data_emissao: e.target.value })} data-testid="input-nf-data" />
        </div>
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-slate-600">Observação (opcional)</label>
        <Input value={nfForm.observacao} onChange={(e) => setNfForm({ ...nfForm, observacao: e.target.value })} placeholder="Ex.: NF de serviço nº 45" />
      </div>
    </div>
  );
}

function NfsSubstituir({ itens, protos, setProtos }) {
  return (
    <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3 space-y-2" data-testid="emissao-substituir">
      <Cabecalho icon={AlertCircle}>NF anterior a cancelar (inscrição unificada)</Cabecalho>
      <p className="text-xs text-red-800">Este município ganhou novas inscrições. Cancele no emissor da prefeitura a NF abaixo e informe o protocolo; depois anexe a nova NF com todos os inscritos.</p>
      {itens.map((s) => (
        <div key={s.nf_numero} className="grid grid-cols-2 gap-2 items-end">
          <div className="text-sm"><b>NF nº {s.nf_numero}</b><div className="text-xs text-slate-500">{formatBRL(s.valor || 0)}{s.nf_data_emissao ? ` · emitida em ${s.nf_data_emissao}` : ""}</div></div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-600">Protocolo de cancelamento*</label>
            <Input value={protos[s.nf_numero] || ""} onChange={(e) => setProtos({ ...protos, [s.nf_numero]: e.target.value })} placeholder="Nº do protocolo" data-testid={`input-protocolo-${s.nf_numero}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmitirNfModal({ ins, onClose, onSaved }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nfForm, setNfForm] = useState(formVazio());
  const [detect, setDetect] = useState({ status: "idle", info: null });
  const [busy, setBusy] = useState(false);
  const [protos, setProtos] = useState({});
  const substituir = ins?.nf_substituir || [];

  useEffect(() => {
    if (!ins) return;
    setNfForm(formVazio(ins)); setDetect({ status: "idle", info: null }); setDados(null); setLoading(true); setProtos({});
    api.get(`/fiscal/inscricoes/${ins.id}/dados-emissao`)
      .then((r) => setDados(r.data))
      .catch(() => toast.error("Falha ao carregar dados de emissão"))
      .finally(() => setLoading(false));
  }, [ins]);

  const detectarNumero = async (file) => {
    setDetect({ status: "detecting", info: null });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: info } = await api.post("/fiscal/detectar-numero-nf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (info?.numero && (info.confianca === "alta" || info.confianca === "media")) {
        setNfForm((f) => ({ ...f, numero: f.numero && f.numero.trim() ? f.numero : String(info.numero) }));
        setDetect({ status: "ok", info });
      } else setDetect({ status: "fail", info });
    } catch (e) {
      setDetect({ status: "fail", info: { erro: e?.response?.data?.detail || "Falha ao processar PDF" } });
    }
  };

  const onFileChange = (file) => {
    setNfForm((f) => ({ ...f, file }));
    if (file && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"))) detectarNumero(file);
    else setDetect({ status: "idle", info: null });
  };

  const salvar = async () => {
    if (!nfForm.file) { toast.error("Selecione o arquivo da NF"); return; }
    if (!nfForm.numero.trim()) { toast.error("Informe o número da NF"); return; }
    if (substituir.some((s) => !(protos[s.nf_numero] || "").trim())) { toast.error("Informe o protocolo de cancelamento da NF anterior"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", nfForm.file);
      const obs = nfForm.observacao ? `&observacao=${encodeURIComponent(nfForm.observacao)}` : "";
      const prot = substituir.length ? `&protocolos_cancelamento=${encodeURIComponent(JSON.stringify(protos))}` : "";
      await api.post(`/fiscal/inscricoes/${ins.id}/nf?numero=${encodeURIComponent(nfForm.numero.trim())}&data_emissao=${nfForm.data_emissao}${obs}${prot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("NF registrada com sucesso 🎉");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar NF");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!ins} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="modal-upload-nf">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-600" /> Emitir Nota Fiscal
            {ins?.alterada_apos_emissao && ins?.nf_status === "emitida" && (
              <span className="ml-2 inline-flex items-center gap-1 bg-red-500 text-white rounded px-2 py-0.5 text-[10px] font-bold uppercase"><AlertCircle className="w-3 h-3" /> Alterada após emissão</span>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading && (
          <div className="py-8 flex flex-col items-center gap-2 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Carregando dados de emissão...</span></div>
        )}
        {!loading && ins && dados && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {substituir.length > 0 && <NfsSubstituir itens={substituir} protos={protos} setProtos={setProtos} />}
            {ins.nf_precisa_revisao && ins.nf_revisoes?.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50/60 p-3 space-y-2" data-testid="emissao-revisoes">
                <Cabecalho icon={AlertCircle}>Revisão pedida pela vendedora</Cabecalho>
                <RevisoesLista ins={ins} soAbertas />
              </div>
            )}
            <Tomador orgao={dados.orgao} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Responsavel resp={dados.responsavel} emailInst={dados.orgao.email_institucional} />
              <Venda dados={dados} rotulo={ins.curso_rotulo} />
            </div>
            <Descricao texto={dados.descricao_nf} />
            <ArquivoNf nfForm={nfForm} setNfForm={setNfForm} detect={detect} onFileChange={onFileChange} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={salvar} disabled={busy || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-salvar-nf">
            <Upload className="w-4 h-4 mr-1" /> {busy ? "Enviando..." : "Salvar NF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
